// apps/web/src/pages/public/ClientDashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  User, Mail, Phone, MapPin, Edit, Save, X,
  FolderOpen, CreditCard, FileText, CheckCircle, Clock,
  Send, RefreshCw, AlertCircle, DollarSign, Upload,
  Download, Eye, ThumbsUp, TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';

// ─── Helpers ──────────────────────────────────────────────────────
const getStatusBadge = (status) => {
  const config = {
    pending: { label: 'Pending', color: 'bg-amber-100 text-amber-800' },
    approved: { label: 'Approved', color: 'bg-green-100 text-green-800' },
    rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800' },
    completed: { label: 'Completed', color: 'bg-blue-100 text-blue-800' },
    active: { label: 'Active', color: 'bg-indigo-100 text-indigo-800' },
    'in-progress': { label: 'In Progress', color: 'bg-purple-100 text-purple-800' },
  };
  return config[status] || { label: status || 'Unknown', color: 'bg-slate-100 text-slate-800' };
};

const getPaymentBadge = (status) => {
  const config = {
    Pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
    Completed: { label: 'Completed', color: 'bg-green-100 text-green-800' },
    Failed: { label: 'Failed', color: 'bg-red-100 text-red-800' },
    Refunded: { label: 'Refunded', color: 'bg-gray-100 text-gray-800' },
  };
  return config[status] || { label: status || 'Unknown', color: 'bg-slate-100 text-slate-800' };
};

const formatPrice = (price) => {
  if (!price) return '₹0';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(price);
};

// ─── Main Component ──────────────────────────────────────────────
const ClientDashboard = () => {
  const { user, isAuthenticated, refreshUser } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [projects, setProjects] = useState([]);
  const [payments, setPayments] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [tasks, setTasks] = useState([]);

  // Profile edit state
  const [isEditing, setIsEditing] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: '',
    phone: '',
    address: '',
  });

  // Requirement form
  const [requirementText, setRequirementText] = useState('');
  const [requirementFiles, setRequirementFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    totalProjects: 0,
    completedProjects: 0,
    totalPayments: 0,
    pendingRequirements: 0,
  });

  // ─── Data fetching ──────────────────────────────────────────
  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      setProfileForm({
        name: user.name || '',
        phone: user.phone || '',
        address: user.address || '',
      });

      // Projects – filtered by leadId
      const projectsData = await pb.collection('projects').getFullList({
        filter: `leadId = "${user.id}"`,
        sort: '-created',
        requestKey: null,
      });
      setProjects(projectsData);

      // Payments – filtered by leadId
      const paymentsData = await pb.collection('payments').getFullList({
        filter: `leadId = "${user.id}"`,
        sort: '-paymentDate',
        requestKey: null,
      });
      setPayments(paymentsData);

      // Requirements
      const reqData = await pb.collection('client_requirements').getFullList({
        filter: `client = "${user.id}"`,
        sort: '-created',
        requestKey: null,
      });
      setRequirements(reqData);

      // Tasks – fetch all tasks for projects belonging to this client
      const projectIds = projectsData.map(p => p.id);
      let tasksData = [];
      if (projectIds.length > 0) {
        tasksData = await pb.collection('project_tasks').getFullList({
          filter: projectIds.map(id => `projectId = "${id}"`).join(' || '),
          sort: 'paymentTermIndex,created',
          expand: 'project,assignedTo,submissions',
          requestKey: null,
        });
      }
      setTasks(tasksData);

      const totalProjects = projectsData.length;
      const completedProjects = projectsData.filter(p => p.status === 'Completed').length;
      const totalPayments = paymentsData
        .filter(p => p.status === 'Completed')
        .reduce((sum, p) => sum + (p.amount || 0), 0);
      const pendingRequirements = reqData.filter(r => r.status === 'pending').length;

      setStats({ totalProjects, completedProjects, totalPayments, pendingRequirements });
    } catch (error) {
      if (!error.isAbort) {
        console.error('Fetch error:', error);
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user, fetchData]);

  // ─── Handlers ──────────────────────────────────────────────
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      await pb.collection('users').update(user.id, {
        name: profileForm.name,
        phone: profileForm.phone,
        address: profileForm.address,
      });
      await refreshUser();
      toast.success('Profile updated');
      setIsEditing(false);
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleSubmitRequirement = async (e) => {
    e.preventDefault();
    if (!requirementText.trim()) {
      toast.error('Please describe your requirement');
      return;
    }
    setSubmitting(true);
    try {
      const data = {
        client: user.id,
        requirement: requirementText,
        status: 'pending',
        submittedAt: new Date().toISOString(),
      };
      await pb.collection('client_requirements').create(data);
      toast.success('Requirement submitted');
      setRequirementText('');
      setRequirementFiles([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Payment Handler ──────────────────────────────────────
  const handlePay = (projectId, termIndex, amount) => {
    navigate('/checkout', {
      state: {
        projectId,
        termIndex,
        amount,
        subscriptionType: 'project_payment',
        redirect: '/client/dashboard',
      },
    });
  };

  // ─── Download Handler ──────────────────────────────────────
  const handleDownload = (submission) => {
    if (!submission?.file) {
      toast.error('No file available to download');
      return;
    }
    try {
      const url = pb.files.getUrl(submission, submission.file);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  // ─── Approve Task Handler ──────────────────────────────────
  const approveTaskMutation = useMutation({
    mutationFn: async (task) => {
      // Update task status to completed
      await pb.collection('project_tasks').update(task.id, {
        status: 'completed',
        completedBy: user.id,
        completedAt: new Date().toISOString(),
      });

      // Create earnings record for designer (if not exists)
      const designerId = task.assignedTo;
      if (designerId && task.price) {
        const amount = task.price * 0.4; // 40% of task price
        const now = new Date();
        const dayOfWeek = now.getDay();
        const diff = (dayOfWeek === 0 ? 6 : dayOfWeek - 1);
        const monday = new Date(now);
        monday.setDate(now.getDate() - diff);
        monday.setHours(0, 0, 0, 0);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        sunday.setHours(23, 59, 59, 999);

        // Check if earning already exists
        const existing = await pb.collection('designer_earnings').getFullList({
          filter: `taskId = "${task.id}"`,
          limit: 1,
        });
        if (existing.length === 0) {
          await pb.collection('designer_earnings').create({
            designerId: designerId,
            taskId: task.id,
            projectId: task.projectId,
            amount: amount,
            status: 'pending',
            weekStart: monday.toISOString(),
            weekEnd: sunday.toISOString(),
          });
        }
      }
    },
    onSuccess: () => {
      toast.success('✅ Design approved! Designer has been credited.');
      queryClient.invalidateQueries(['tasks']);
      fetchData();
    },
    onError: (err) => {
      toast.error('Failed to approve: ' + err.message);
    },
  });

  const handleApproveTask = (task) => {
    if (window.confirm(`Approve design for "${task.title}"? This will mark the task as completed and credit the designer.`)) {
      approveTaskMutation.mutate(task);
    }
  };

  // ─── Redirects ─────────────────────────────────────────────
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'client') {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Access Denied</h2>
          <p className="text-slate-600">You are not registered as a client.</p>
          <p className="text-sm text-slate-400 mt-2">Your current role: <strong>{user?.role || 'N/A'}</strong></p>
          <button onClick={() => window.location.href = '/'} className="mt-6 px-6 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition">
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-slate-500 font-medium">Loading dashboard...</p>
      </div>
    );
  }

  // ─── Render ────────────────────────────────────────────────
  return (
    <>
      <Helmet>
        <title>My Dashboard | HouseDesigns99</title>
        <meta name="description" content="Manage your projects, payments, and requirements." />
      </Helmet>

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <User className="w-6 h-6 text-amber-600" />
            My Dashboard
          </h1>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 text-slate-400 hover:text-amber-600 transition rounded-lg hover:bg-amber-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard title="Total Projects" value={stats.totalProjects} icon={FolderOpen} color="indigo" />
          <StatCard title="Completed" value={stats.completedProjects} icon={CheckCircle} color="green" />
          <StatCard title="Total Paid" value={`₹${stats.totalPayments.toLocaleString()}`} icon={DollarSign} color="emerald" />
          <StatCard title="Pending Requirements" value={stats.pendingRequirements} icon={AlertCircle} color="amber" />
        </div>

        <ProfileCard
          user={user}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          profileForm={profileForm}
          setProfileForm={setProfileForm}
          handleUpdateProfile={handleUpdateProfile}
        />

        <div className="space-y-6">
          {projects.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <p className="text-sm text-slate-400 text-center py-6">No projects yet.</p>
            </div>
          ) : (
            projects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                payments={payments}
                tasks={tasks.filter(t => t.projectId === project.id)}
                onPay={handlePay}
                onDownload={handleDownload}
                onApprove={handleApproveTask}
                isApproving={approveTaskMutation.isPending}
              />
            ))
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <RequirementSubmit
            requirementText={requirementText}
            setRequirementText={setRequirementText}
            requirementFiles={requirementFiles}
            setRequirementFiles={setRequirementFiles}
            submitting={submitting}
            handleSubmitRequirement={handleSubmitRequirement}
          />
          <RequirementHistory requirements={requirements} />
        </div>
      </div>
    </>
  );
};

// ─── Subcomponents ──────────────────────────────────────────────

const StatCard = ({ title, value, icon: Icon, color }) => {
  const colors = {
    indigo: 'bg-indigo-50 text-indigo-600',
    green: 'bg-green-50 text-green-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
  };
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 hover:shadow-md transition">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500">{title}</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">{value}</p>
        </div>
        <div className={`p-3 rounded-full ${colors[color] || 'bg-slate-50 text-slate-600'}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
};

const ProfileCard = ({ user, isEditing, setIsEditing, profileForm, setProfileForm, handleUpdateProfile }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
          <User className="w-5 h-5 text-amber-600" /> Profile
        </h2>
        <button
          onClick={() => setIsEditing(!isEditing)}
          className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition"
        >
          {isEditing ? <X className="w-4 h-4" /> : <Edit className="w-4 h-4" />}
        </button>
      </div>

      {isEditing ? (
        <form onSubmit={handleUpdateProfile} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
            <input
              type="text"
              value={profileForm.name}
              onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-500 cursor-not-allowed"
            />
            <p className="text-xs text-slate-400 mt-1">Email cannot be changed</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
            <input
              type="text"
              value={profileForm.phone}
              onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
            <textarea
              value={profileForm.address}
              onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
              rows="2"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none"
            />
          </div>
          <div className="md:col-span-2 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition flex items-center gap-2"
            >
              <Save className="w-4 h-4" /> Save Changes
            </button>
          </div>
        </form>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div className="flex items-center gap-2"><User className="w-4 h-4 text-slate-400" /> {user?.name}</div>
          <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400" /> {user?.email}</div>
          <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" /> {user?.phone || 'Not provided'}</div>
          <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-400" /> {user?.address || 'Not provided'}</div>
        </div>
      )}
    </div>
  );
};

// ─── Project Card (with Tasks) ──────────────────────────────────
const ProjectCard = ({ project, payments, tasks, onPay, onDownload, onApprove, isApproving }) => {
  const progress = project.progress || 0;
  const paidAmount = payments
    .filter(p => p.projectId === project.id && p.status === 'Completed')
    .reduce((sum, p) => sum + p.amount, 0);
  const paymentTerms = project.paymentTerms || [{ term: 1, amount: project.finalAmount || project.totalAmount }];

  // Check if a specific term is paid
  const isTermPaid = (termIndex) => {
    return payments.some(p => p.projectId === project.id && p.termIndex === termIndex && p.status === 'Completed');
  };

  return (
    <Card className="border-0 shadow-sm overflow-hidden">
      <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-100">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <CardTitle className="text-xl font-bold text-slate-800">{project.name}</CardTitle>
            <p className="text-sm text-slate-500">
              {project.packageType} • {project.buildingType}
            </p>
          </div>
          <Badge variant="outline" className={project.status === 'Completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100'}>
            {project.status}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        {/* Progress Bar */}
        <div>
          <div className="flex justify-between text-xs text-slate-400">
            <span>Progress</span>
            <span>{progress}%</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-2 mt-1">
            <div className="bg-amber-500 h-2 rounded-full transition-all" style={{ width: `${Math.min(progress, 100)}%` }} />
          </div>
        </div>

        {/* Payment Terms */}
        <div>
          <p className="text-xs font-medium text-slate-500 mb-2">Payment Terms:</p>
          <div className="space-y-2">
            {paymentTerms.map((term, idx) => {
              const paid = isTermPaid(term.term);
              return (
                <div key={idx} className="flex items-center justify-between bg-slate-50 p-2 rounded-lg">
                  <div>
                    <span className="text-sm font-medium">Term {term.term}</span>
                    <span className="text-sm text-slate-500 ml-2">{formatPrice(term.amount)}</span>
                    {paid && <CheckCircle className="w-4 h-4 text-green-500 inline ml-2" />}
                  </div>
                  {paid ? (
                    <span className="text-xs text-green-600 font-medium">✅ Paid</span>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => onPay(project.id, term.term, term.amount)}
                      className="bg-amber-500 hover:bg-amber-600 text-white"
                    >
                      Pay Now
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-2 flex justify-between text-sm">
            <span className="text-slate-500">Total: {formatPrice(project.finalAmount || project.totalAmount)}</span>
            <span className="text-green-600">Paid: {formatPrice(paidAmount)}</span>
          </div>
        </div>

        {/* Tasks */}
        {tasks.length > 0 && (
          <div>
            <p className="text-xs font-medium text-slate-500 mb-2">Design Tasks:</p>
            <div className="space-y-2">
              {tasks.map((task) => {
                const isPaid = isTermPaid(task.paymentTermIndex || 1);
                const hasSubmission = task.expand?.submissions && task.expand.submissions.length > 0;
                const latestSubmission = hasSubmission ? task.expand.submissions[task.expand.submissions.length - 1] : null;
                const isSubmitted = task.status === 'submitted';
                const isCompleted = task.status === 'completed';
                const canDownload = isPaid && hasSubmission;
                const canApprove = isPaid && isSubmitted && !isCompleted;

                return (
                  <div key={task.id} className="border border-slate-200 rounded-lg p-3 bg-white">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800 text-sm">{task.title}</p>
                        <p className="text-xs text-slate-500">
                          Term {task.paymentTermIndex || 1} • {task.category || 'Design'}
                        </p>
                        {task.assignedTo && (
                          <p className="text-xs text-slate-400">Designer: {task.expand?.assignedTo?.name || 'Assigned'}</p>
                        )}
                      </div>
                      <Badge variant="outline" className={
                        isCompleted ? 'bg-green-100 text-green-700' :
                        isSubmitted ? 'bg-purple-100 text-purple-700' :
                        'bg-amber-100 text-amber-700'
                      }>
                        {isCompleted ? 'Approved' : isSubmitted ? 'Submitted' : 'Pending'}
                      </Badge>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 mt-2">
                      {canDownload && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onDownload(latestSubmission)}
                          className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                        >
                          <Download className="w-4 h-4 mr-1" /> Download
                        </Button>
                      )}
                      {canApprove && (
                        <Button
                          size="sm"
                          onClick={() => onApprove(task)}
                          disabled={isApproving}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <ThumbsUp className="w-4 h-4 mr-1" /> Approve Design
                        </Button>
                      )}
                      {!canDownload && !canApprove && (
                        <span className="text-xs text-slate-400 italic">
                          {!isPaid ? 'Payment required to access' : 'Waiting for design submission'}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Payments List ──────────────────────────────────────────────
const PaymentsList = ({ payments }) => {
  if (payments.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-amber-600" /> Payment History
        </h2>
        <p className="text-sm text-slate-400 text-center py-6">No payments recorded.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
      <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
        <CreditCard className="w-5 h-5 text-amber-600" /> Payment History
      </h2>
      <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
        {payments.map((payment) => {
          const status = getPaymentBadge(payment.status);
          return (
            <div key={payment.id} className="flex flex-wrap items-center justify-between border-b border-slate-100 pb-3 last:border-0">
              <div>
                <p className="font-medium text-slate-800">₹{payment.amount?.toLocaleString() || 0}</p>
                <p className="text-xs text-slate-400">
                  {payment.paymentMethod || 'N/A'} • {new Date(payment.paymentDate).toLocaleDateString()}
                  {payment.projectId && <span className="ml-1">(Project)</span>}
                  {payment.termIndex && <span className="ml-1">Term {payment.termIndex}</span>}
                </p>
              </div>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${status.color}`}>
                {status.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Requirement Submit & History (unchanged) ──────────────────
const RequirementSubmit = ({ requirementText, setRequirementText, requirementFiles, setRequirementFiles, submitting, handleSubmitRequirement }) => {
  const handleFileChange = (e) => {
    setRequirementFiles(Array.from(e.target.files));
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
      <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
        <FileText className="w-5 h-5 text-amber-600" /> Submit Requirement
      </h2>
      <form onSubmit={handleSubmitRequirement} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Describe your requirement *</label>
          <textarea
            value={requirementText}
            onChange={(e) => setRequirementText(e.target.value)}
            rows="4"
            placeholder="e.g., I need a 2BHK house plan with Vastu compliance..."
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Reference Images (optional)</label>
          <div className="flex items-center gap-2">
            <label className="cursor-pointer px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition flex items-center gap-2">
              <Upload className="w-4 h-4" />
              <span className="text-sm">Choose files</span>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
            {requirementFiles.length > 0 && (
              <span className="text-sm text-slate-500">{requirementFiles.length} file(s) selected</span>
            )}
          </div>
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition flex items-center gap-2 disabled:opacity-50"
        >
          <Send className="w-4 h-4" /> {submitting ? 'Submitting...' : 'Submit Requirement'}
        </button>
      </form>
    </div>
  );
};

const RequirementHistory = ({ requirements }) => {
  if (requirements.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-amber-600" /> Requirement History
        </h2>
        <p className="text-sm text-slate-400 text-center py-6">No requirements submitted yet.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
      <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
        <Clock className="w-5 h-5 text-amber-600" /> Requirement History
      </h2>
      <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
        {requirements.map((req) => {
          const status = getStatusBadge(req.status);
          return (
            <div key={req.id} className="border border-slate-200 rounded-lg p-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <p className="text-sm text-slate-700 flex-1">{req.requirement}</p>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${status.color}`}>
                  {status.label}
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-1">{new Date(req.created).toLocaleString()}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ClientDashboard;